import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import { NgFor } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { HxUiLabelPipe, isoDate, StoreBasicModel } from 'hx-services';
import { HxStoreSelectComponent } from 'hx-component';
import { endOfMonth, format, parseISO, startOfMonth, setMonth } from 'date-fns';
import { enUS, kk, ru } from 'date-fns/locale';
import { Locale } from 'date-fns';
import { CopyPropertiesOperation, VanillaService } from '@manager-app/service/vanilla.service';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';
import { HcToastrService } from '@manager-app/service/toastr.service';

interface MonthOption {
  index: number;
  title: string;
}

@Component({
  selector: 'app-copy-limits-modal',
  templateUrl: './copy-limits.modal.html',
  styleUrls: ['./copy-limits.modal.css'],
  imports: [FormsModule, NgFor, TranslocoPipe, HxStoreSelectComponent, HxUiLabelPipe]
})
export class CopyLimitsModal implements OnInit {
  @Input() targetStore!: StoreBasicModel;
  @Input() targetStoreId!: number;
  @Input() currentMonthDate!: string;

  sourceStoreId?: number;
  monthOptions: MonthOption[] = [];
  selectedMonthIndex = 0;
  selectedYear = new Date().getFullYear();
  operation: CopyPropertiesOperation = 'APPLY_ALL';
  isLoading = {
    save: false,
  };

  filterStores = (storeList: StoreBasicModel[]): StoreBasicModel[] => {
    return storeList.filter(store => store.id !== this.targetStoreId);
  };

  constructor(
    private modalInstance: NgbActiveModal,
    private vanillaService: VanillaService,
    private errorService: ErrorHandlerService,
    private toastr: HcToastrService,
    private translocoService: TranslocoService,
  ) {
  }

  ngOnInit(): void {
    const current = parseISO(this.currentMonthDate);
    this.selectedYear = current.getFullYear();
    this.selectedMonthIndex = current.getMonth();
    const localeMap: Record<string, Locale> = { ru, kk, en: enUS };
    const locale = localeMap[this.translocoService.getActiveLang()] ?? ru;
    for (let index = 0; index < 12; index++) {
      this.monthOptions.push({
        index,
        title: format(setMonth(current, index), 'LLLL', { locale }),
      });
    }
  }

  onSourceStoreChanged(store?: StoreBasicModel | StoreBasicModel[]): void {
    if (store && !Array.isArray(store)) {
      this.sourceStoreId = store.id;
    } else {
      this.sourceStoreId = undefined;
    }
  }

  dismissModal(): void {
    this.modalInstance.dismiss();
  }

  submit(): void {
    if (!this.sourceStoreId || this.sourceStoreId === this.targetStoreId) {
      this.toastr.error('copyLimits.error.sameStore');
      return;
    }
    const base = setMonth(new Date(this.selectedYear, 0, 1), this.selectedMonthIndex);
    const dateRange = {
      from: isoDate(startOfMonth(base)),
      to: isoDate(endOfMonth(base)),
    };
    this.isLoading.save = true;
    this.vanillaService.copyProperties({
      sourceStoreId: this.sourceStoreId,
      targetStoreId: this.targetStoreId,
      dateRange,
      operation: this.operation,
    }).subscribe({
      next: (result: unknown[]) => {
        this.isLoading.save = false;
        if (result.length === 0) {
          this.toastr.info('copyLimits.empty');
          this.modalInstance.close();
        } else {
          this.toastr.success('copyLimits.success');
          this.modalInstance.close(dateRange.from);
        }
      },
      error: (err) => {
        this.isLoading.save = false;
        this.errorService.handler(err.error);
      },
    });
  }
}
